import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, 
  UtensilsCrossed, 
  Truck, 
  ShoppingBag, 
  User, 
  ChefHat,
  LogOut,
  Settings,
  Award,
  Globe,
  Coffee,
  Bell,
  Check,
  ClipboardList
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useNotifications } from '../context/NotificationContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import LocationReminder from './LocationReminder';
import LoyaltyBadge from './LoyaltyBadge';
import MenuChatbot from './MenuChatbot';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout, loyaltyPoints } = useAuth();
  const { totalItems } = useCart();
  const { language, setLanguage, t, languages } = useLanguage();
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();

  const navItems = [
    { path: '/', icon: Home, label: t('home') },
    { path: '/alakart', icon: UtensilsCrossed, label: t('alakart') },
    { path: '/catering', icon: ChefHat, label: t('catering') },
    { path: '/icecekler', icon: Coffee, label: t('beverages') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-bone">
      {/* Location Reminder */}
      <LocationReminder />
      {/* Desktop Header */}
      <header className="sticky top-0 bg-bone/80 backdrop-blur-md z-50 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Central Restaurant" className="h-10 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-terracotta'
                      : 'text-charcoal/70 hover:text-charcoal'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1 hover:bg-stone-100">
                    <Globe className="w-4 h-4 text-charcoal" />
                    <span className="text-sm font-medium">{languages.find(l => l.code === language)?.flag}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`flex items-center gap-2 cursor-pointer ${language === lang.code ? 'bg-basil/10' : ''}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Cart */}
              <Link to="/cart" className="relative p-2 hover:bg-stone-100 rounded-full transition-colors">
                <ShoppingBag className="w-5 h-5 text-charcoal" />
                {totalItems > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-terracotta text-white text-xs rounded-full flex items-center justify-center font-medium"
                  >
                    {totalItems}
                  </motion.span>
                )}
              </Link>

              {/* Notification Bell */}
              {isAuthenticated && !isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="relative p-2 hover:bg-stone-100 rounded-full transition-colors" data-testid="notification-bell-btn">
                      <Bell className="w-5 h-5 text-charcoal" />
                      {unreadCount > 0 && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium"
                          data-testid="notification-badge"
                        >
                          {unreadCount}
                        </motion.span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 max-h-[400px] overflow-y-auto">
                    <div className="flex items-center justify-between px-3 py-2 border-b">
                      <span className="font-semibold text-sm text-charcoal">Bildirimler</span>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-basil font-medium hover:underline" data-testid="mark-all-read-btn">
                          Tumunu Oku
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center text-charcoal/40 text-sm">Bildirim yok</div>
                    ) : (
                      notifications.slice(0, 20).map((n) => (
                        <DropdownMenuItem
                          key={n.id}
                          onClick={() => { markRead(n.id); if (n.order_id) navigate(`/orders/${n.order_id}`); }}
                          className={`flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer ${!n.is_read ? 'bg-basil/5' : ''}`}
                          data-testid={`notification-item-${n.id}`}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${!n.is_read ? 'bg-basil' : 'bg-transparent'}`} />
                            <span className="font-medium text-sm text-charcoal flex-1">{n.title}</span>
                            <NotifStatusBadge status={n.status} />
                          </div>
                          <span className="text-xs text-charcoal/60 ml-4">{n.message}</span>
                          <span className="text-xs text-charcoal/40 ml-4">{formatTimeAgo(n.created_at)}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* User Menu */}
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 hover:bg-stone-100"
                      data-testid="user-menu-btn"
                    >
                      <div className="w-8 h-8 bg-basil rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <span className="hidden sm:block text-sm font-medium text-charcoal">
                        {user?.name}
                      </span>
                      {!isAdmin && (
                        <LoyaltyBadge totalOrders={user?.total_orders || 0} size="sm" variant="icon" className="hidden sm:inline-flex" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Loyalty Points + Tier Banner */}
                    <div 
                      onClick={() => navigate('/loyalty')}
                      className="px-3 py-2 mb-1 bg-gradient-to-r from-basil/10 to-basil/5 rounded-md cursor-pointer hover:from-basil/20 hover:to-basil/10 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-charcoal/60">{t('loyaltyPoints') || 'Sadakat Puanı'}</span>
                        {!isAdmin && <LoyaltyBadge totalOrders={user?.total_orders || 0} size="sm" variant="pill" />}
                      </div>
                      <p className="font-bold text-basil mt-1">{loyaltyPoints} {language === 'tr' ? 'puan' : language === 'me' ? 'poena' : 'points'}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                      <User className="w-4 h-4 mr-2" />
                      {t('profile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/loyalty')} data-testid="loyalty-menu-item">
                      <Award className="w-4 h-4 mr-2" />
                      {t('loyaltyProgram') || 'Sadakat Programı'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="orders-menu-item">
                      <Truck className="w-4 h-4 mr-2" />
                      {t('orders')}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Panel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/waiter')} data-testid="waiter-screen-menu-item">
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Garson Ekrani
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login">
                  <Button 
                    className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full px-6"
                    data-testid="login-btn"
                  >
                    {t('login')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`${location.pathname === '/catering' ? 'pb-0' : 'pb-20'} md:pb-8`}>
        {children}
      </main>

      {/* Mobile Bottom Navigation - Hide on Catering page */}
      {location.pathname !== '/catering' && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-stone-100 flex justify-around py-2 z-50 safe-area-bottom">
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors ${
                isActive ? 'text-terracotta' : 'text-charcoal/60'
              }`}
            >
              <item.icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </Link>
          );
        })}
        <Link
          to="/cart"
          className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors relative ${
            location.pathname === '/cart' ? 'text-terracotta' : 'text-charcoal/60'
          }`}
        >
          <ShoppingBag className={`w-5 h-5 ${location.pathname === '/cart' ? 'stroke-[2.5]' : ''}`} />
          {totalItems > 0 && (
            <span className="absolute top-1 right-2 w-4 h-4 bg-terracotta text-white text-[10px] rounded-full flex items-center justify-center font-medium">
              {totalItems}
            </span>
          )}
          <span className="text-xs mt-1 font-medium">{t('cart')}</span>
        </Link>
        <Link
          to={isAuthenticated ? '/profile' : '/login'}
          className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors ${
            ['/profile', '/login', '/orders'].includes(location.pathname) ? 'text-terracotta' : 'text-charcoal/60'
          }`}
        >
          <User className={`w-5 h-5 ${['/profile', '/login', '/orders'].includes(location.pathname) ? 'stroke-[2.5]' : ''}`} />
          <span className="text-xs mt-1 font-medium">{isAuthenticated ? t('profile') : t('login')}</span>
        </Link>
      </nav>
      )}

      {/* AI Menu Chatbot — public-facing pages only */}
      {!['/admin', '/waiter', '/login'].some(p => location.pathname.startsWith(p)) && <MenuChatbot />}
    </div>
  );
};

export default Layout;

const STATUS_COLORS = {
  hazirlaniyor: 'bg-amber-100 text-amber-700',
  yolda: 'bg-blue-100 text-blue-700',
  teslim_edildi: 'bg-green-100 text-green-700',
  iptal: 'bg-red-100 text-red-700'
};
const STATUS_LABELS = {
  hazirlaniyor: 'Hazirlaniyor',
  yolda: 'Yolda',
  teslim_edildi: 'Teslim Edildi',
  iptal: 'Iptal'
};

const NotifStatusBadge = ({ status }) => {
  if (!status) return null;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[status] || 'bg-stone-100 text-charcoal/60'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Az once';
  if (mins < 60) return `${mins} dk once`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} saat once`;
  return `${Math.floor(hours / 24)} gun once`;
};
